import { useState, useEffect } from "react";
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Checkbox,
  FormControlLabel,
  IconButton,
  Link,
  SpeedDial,
  SpeedDialAction,
  CircularProgress,
  TextField,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DownloadIcon from "@mui/icons-material/Download";
import EmailIcon from "@mui/icons-material/Email";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from '@mui/icons-material/Clear';
import CustomDataTable from "./CustomDataTable";
import ComposeMsgDialog from "./ComposeMsgDialog";
import convertLabel from "../../../utilities/convertLabel";
import request from "../../../utilities/requests";
import { toast } from "../../../utilities/SnackBarUtils";

const eventFields = [
  { label: "Event City", value: "city" },
  { label: "Event State", value: "region" },
  { label: "Event Zip", value: "zip" },
  { label: "Event Date", value: "eventDate" },
  { label: "Venue", value: "venue" },
  { label: "Event Name", value: "eventName" },
];

const operators = [
  { label: "Equals", value: "equals" },
  { label: "Greater than", value: "gt" },
  { label: "Less than", value: "lt" },
  { label: "Contains", value: "contains" },
];

const defaultFields = ["email", "firstName", "lastName"];

const CustomReporting = ({}) => {
  const [profileQuestions, setProfileQuestions] = useState([]);
  const [uniqueUsers, setUniqueUsers] = useState(true);
  const [filters, setFilters] = useState([
    {
      dimension: "",
      fields: [],
      field: "",
      operator: "",
      options: [],
      value: "",
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [fields, setFields] = useState([]);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(null);
  const [downloadLink, setDownloadLink] = useState(null);
  const [fieldsDialogOpen, setFieldsDialogOpen] = useState(false);
  const [saveQueryOpen, setSaveQueryOpen] = useState(false);
  const [queryName, setQueryName] = useState("");
  const [savedQueries, setSavedQueries] = useState([]);
  const [composeOpen, setComposeOpen] = useState(false);
  const [sendData, setSendData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedQueryId, setSelectedQueryId] = useState("");
  const [deleteQueryOpen, setDeleteQueryOpen] = useState(false);

  const handleOpenFieldsDialog = () => setFieldsDialogOpen(true);
  const handleCloseFieldsDialog = () => setFieldsDialogOpen(false);

  const handleToggleField = (index) => {
    setFields((prev) =>
      prev.map((f, i) => (i === index ? { ...f, checked: !f.checked } : f))
    );
  };

  const defaultOrder = ["email", "firstName", "lastName"];

  const [page, setPage] = useState(1);
  const limit = 100; // match server default

  useEffect(() => {
    getProfileQuestions();
    getFields();
    getSavedQueries();
  }, []);

  const getProfileQuestions = async () => {
    const result = await request("profilequestions");
    if (result.success) {
      setProfileQuestions(result.questions);
    }
  };

  const getFields = async (index, field) => {
    const result = await request(`reports/fields`);
    if (result.success) {
      let fs = result.fields.map((f) => {
        return {
          label: convertLabel(f),
          value: f,
          checked: defaultFields.includes(f),
        };
      });

      setFields(fs);
    }
  };

  const getValues = async (index, field) => {
    const result = await request(`reports/fieldvalues/?field=${field}`);
    if (result.success) {
      setFilters((prev) =>
        prev.map((f, i) => (i === index ? { ...f, options: result.values } : f))
      );
    }
  };

  const getSavedQueries = async () => {
    const result = await request("reports/savedqueries");
    if (result.success) setSavedQueries(result.queries);
  };

  const handleSavedQueryChange = async (e) => {
    const id = e.target.value;
    setSelectedQueryId(id);
    if (!id) return;
    const result = await request(`reports/runsavedquery/?id=${id}`);
    if (result.success) {
      setData(result.data);
      setTotal(result.total);
      const {
        filters: savedFilters,
        selectedFields,
        uniqueUsers: uq,
      } = result.query;
      // enhance saved filters with fields list and options array
      const loadedFilters = savedFilters.map((f) => {
        // determine available fields for this filter
        let fieldsList = [];
        if (f.dimension === "profilequestion") {
          fieldsList = profileQuestions.map((q) => ({
            label: q.systemLabel,
            value: q.systemLabel,
          }));
        } else if (f.dimension === "event") {
          fieldsList = eventFields;
        }
        // determine options for existing field selection
        let optionsList = [];
        if (f.dimension === "profilequestion") {
          const question = profileQuestions.find(
            (q) => q.systemLabel === f.field
          );
          if (question) {
            if (question.variant === "age") {
              const len =
                question.upperRangeValue - question.lowerRangeValue + 1;
              optionsList = [...Array(len).keys()].map(
                (i) => question.lowerRangeValue + i
              );
            } else {
              optionsList = question.choices.map((c) => c.value);
            }
          }
        }
        // for event dimension, leave options empty (will fetch on change)
        return {
          ...f,
          fields: fieldsList,
          options: optionsList,
        };
      });
      setFilters(loadedFilters);
      // update checked fields based on selectedFields objects
      setFields((prev) =>
        prev.map((f) => ({
          ...f,
          checked: selectedFields.some((sf) => sf.value === f.value),
        }))
      );
      // set unique checkbox
      setUniqueUsers(uq);
      // reset page and run report
      setPage(1);
    }
  };

  const handleDimensionChange = (index, dimension) => {
    let fieldsList = [];
    if (dimension === "profilequestion") {
      fieldsList = profileQuestions.map((q) => ({
        label: q.systemLabel,
        value: q.systemLabel,
      }));
    } else if (dimension === "event") {
      fieldsList = eventFields;
    }
    setFilters((prev) =>
      prev.map((f, i) =>
        i === index
          ? { dimension, fields: fieldsList, field: "", options: [], value: "" }
          : f
      )
    );
  };

  const handleFieldChange = (index, field) => {
    // determine new options based on dimension
    const prevFilter = filters[index];
    let newOptions = [];
    if (prevFilter.dimension === "profilequestion") {
      // pull choices locally
      const question = profileQuestions.find((q) => q.systemLabel === field);
      if (question.variant === "age") {
        newOptions = [
          ...Array(
            question.upperRangeValue - question.lowerRangeValue + 1
          ).keys(),
        ].map((i) => question.lowerRangeValue + i);
      } else {
        newOptions = question ? question.choices.map((c) => c.value) : [];
      }
    }

    // update filters state
    setFilters((prev) =>
      prev.map((f, i) =>
        i === index ? { ...f, field, options: newOptions, value: "" } : f
      )
    );

    // for event dimension, fetch values from server
    if (prevFilter.dimension === "event") {
      getValues(index, field);
    }
  };

  const handleValueChange = (index, value) => {
    setFilters((prev) =>
      prev.map((f, i) => (i === index ? { ...f, value } : f))
    );
  };

  const handleOperatorChange = (index, operator) => {
    setFilters((prev) =>
      prev.map((f, i) => (i === index ? { ...f, operator, value: "" } : f))
    );
  };

  const addFilter = () => {
    setFilters((prev) => [
      ...prev,
      {
        dimension: "",
        fields: [],
        field: "",
        operator: "",
        options: [],
        value: "",
      },
    ]);
  };


  const handleRemoveFilter = (index) => {
    setFilters((prev) => {
      if (prev.length === 1) {
        // Clear the single filter but keep the element
        return [
          {
            dimension: "",
            fields: [],
            field: "",
            operator: "",
            options: [],
            value: "",
          },
        ];
      }
      // Remove the filter at the given index
      return prev.filter((_, i) => i !== index);
    });
  };

  // Helper to reset all filters and state to initial
  const resetFilters = () => {
    // Reset the single filter to its initial blank state
    setFilters([
      {
        dimension: "",
        fields: [],
        field: "",
        operator: "",
        options: [],
        value: "",
      },
    ]);
    // Reset selected fields back to defaults
    setFields(prevFields =>
      prevFields.map(f => ({
        ...f,
        checked: defaultFields.includes(f.value),
      }))
    );
    // Reset uniqueUsers checkbox
    setUniqueUsers(true);
    // Clear any selected saved query
    setSelectedQueryId("");
    // Reset pagination
    setPage(1);
    // Clear existing data and total
    setData([]);
    setTotal(null);
    // Re-fetch the full fields list to repopulate options
    getFields();
  };

  const getQuery = () => {
    const fs = filters.map((f) => ({
      dimension: f.dimension,
      field: f.field,
      operator: f.operator,
      value: f.value,
    }));
    const selectedFields = fields.filter((f) => f.checked);
    const query = { filters: fs, selectedFields, uniqueUsers };
    return query;
  };

  const runReport = async (pageNum = page) => {
    setLoading(true);
    const body = getQuery();

    // include pagination query params
    const result = await request(
      `reports/custom?page=${pageNum}&limit=${limit}`,
      "post",
      body
    );
    if (result.success) {
      setData(result.data);
      setTotal(result.total);
    }
    setLoading(false);
  };

  const handleSaveQuery = async () => {
    const query = getQuery();
    const body = { name: queryName, query };
    const result = await request("reports/savequery", "post", body);
    if (result.success) {
      getSavedQueries();
      toast.success("Query saved!");
      setSaveQueryOpen(false);
      setQueryName("");
    }
  };

  const handleDeleteQuery = async () => {
    const result = await request(`reports/deletequery/${selectedQueryId}`, 'post',{});
    if (result.success) {
      setDeleteQueryOpen(false);
      resetFilters();
      getSavedQueries();
      toast.success("Query deleted.")
    }
  };

  const handleExport = async (action) => {
    const body = getQuery();
    let url = `reports/export?reportName=custom`;

    const result = await request(url, "post", body);
    if (result.success) {
      setDownloadLink(result.url);
    }
  };

  const handleSend = (data) => {
    setSendData(data);
    setConfirmOpen(true);
  };

  const handleConfirmSend = async () => {
    setSending(true);
    const fs = filters.map((f) => ({
      dimension: f.dimension,
      field: f.field,
      operator: f.operator,
      value: f.value,
    }));
    const selectedFields = fields.filter((f) => f.checked);
    const body = {
      filters: fs,
      selectedFields,
      uniqueUsers,
      templateData: sendData,
    };
    const result = await request("templates/customsend", "post", body);
    if (result.success) {
      setConfirmOpen(false);
      setComposeOpen(false);
      toast.success(result.message);
    }
    setSending(false);
  };

  return (
    <Grid container style={{ padding: 20 }} spacing={2}>
      {/* Left column: filters */}
      <Grid item xs={3}>
        <Grid container direction="column" spacing={1}>
          <Grid
            item
            style={{
              paddingBottom: 20,
              display: "flex",
              flexDirection: "row",
              gap: 0,
            }}
          >
            <Button
              variant="contained"
              onClick={() => {
                setPage(1);
                runReport(1);
              }}
              disabled={!filters.some((f) => f.field && f.operator && f.value)}
              style={{ width: 130 }}
            >
              {loading ? (
                <CircularProgress style={{ color: "white" }} size={25} />
              ) : (
                "Run Report"
              )}
            </Button>
            <IconButton
              size="small"
              onClick={handleOpenFieldsDialog}
              sx={{ ml: 1 }}
            >
              <FilterListIcon />
            </IconButton>
          </Grid>
          <Grid item>
            <FormControl fullWidth margin="dense">
              <InputLabel
                shrink
                style={{
                  backgroundColor: "white",
                  paddingLeft: 4,
                  paddingRight: 4,
                }}
              >
                Saved Query
              </InputLabel>
              <Select
                size="small"
                value={selectedQueryId}
                label="Saved Query"
                onChange={handleSavedQueryChange}
              >
                <MenuItem value="">None</MenuItem>
                {savedQueries.map((q) => (
                  <MenuItem key={q._id} value={q._id}>
                    {q.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <Tooltip title="Only include one record per user by email">
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={uniqueUsers}
                    onChange={() => setUniqueUsers((u) => !u)}
                  />
                }
                label="Unique Users"
              />
            </Tooltip>
          </Grid>

          {filters.map((filter, idx) => (
            <Grid item key={idx}>
              <Paper elevation={2} style={{ padding: 8 }}>
                <Grid container spacing={1} direction="column">
                  <Grid item>
                    <FormControl fullWidth margin="dense">
                      <InputLabel shrink style={{ backgroundColor: "white" }}>
                        Dimension
                      </InputLabel>
                      <Select
                        size="small"
                        value={filter.dimension}
                        label="Dimension"
                        onChange={(e) =>
                          handleDimensionChange(idx, e.target.value)
                        }
                      >
                        <MenuItem value="profilequestion">
                          Profile Question
                        </MenuItem>
                        <MenuItem value="event">Event</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item>
                    <FormControl
                      fullWidth
                      margin="dense"
                      disabled={!filter.fields.length}
                    >
                      <InputLabel shrink style={{ backgroundColor: "white" }}>
                        Field
                      </InputLabel>
                      <Select
                        size="small"
                        value={filter.field}
                        label="Field"
                        onChange={(e) => handleFieldChange(idx, e.target.value)}
                      >
                        {filter.fields.map((f) => (
                          <MenuItem key={f.value} value={f.value}>
                            {f.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item>
                    <FormControl
                      fullWidth
                      margin="dense"
                      disabled={!filter.field}
                    >
                      <InputLabel shrink style={{ backgroundColor: "white" }}>
                        Operator
                      </InputLabel>
                      <Select
                        size="small"
                        value={filter.operator}
                        label="Operator"
                        onChange={(e) =>
                          handleOperatorChange(idx, e.target.value)
                        }
                      >
                        {operators.map((op) => (
                          <MenuItem key={op.value} value={op.value}>
                            {op.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item>
                    <FormControl
                      fullWidth
                      margin="dense"
                      disabled={!filter.options.length}
                    >
                      <InputLabel shrink style={{ backgroundColor: "white" }}>
                        Value
                      </InputLabel>
                      <Select
                        size="small"
                        value={filter.value}
                        label="Value"
                        onChange={(e) => handleValueChange(idx, e.target.value)}
                      >
                        {filter.options.map((val) => (
                          <MenuItem key={val} value={val}>
                            {val}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleRemoveFilter(idx)}
                    >
                      Remove
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}
          <Grid item>
            <Button variant="outlined" onClick={addFilter}>
              Add Filter
            </Button>
          </Grid>
        </Grid>
      </Grid>

      {/* Right column: data table placeholder */}
      <Grid item xs={9}>
        {total && (
          <Grid
            container
            justifyContent="flex-end"
            style={{ marginBottom: 8, paddingRight: 20 }}
          >
            {downloadLink ? (
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Link href={downloadLink}>Download Report</Link>
              </div>
            ) : null}
            <Typography>{total} Results</Typography>
          </Grid>
        )}
        <div style={{ padding: 20, minHeight: "300px" }}>
          <CustomDataTable data={data} fields={fields} />
          {data.length > 0 ? (
            <Grid container justifyContent="center" style={{ marginTop: 16 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  if (page > 1) {
                    const newPage = page - 1;
                    setPage(newPage);
                    runReport(newPage);
                  }
                }}
                disabled={page === 1}
                style={{ marginRight: 8 }}
              >
                Previous
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  if (data.length === limit) {
                    const newPage = page + 1;
                    setPage(newPage);
                    runReport(newPage);
                  }
                }}
                disabled={data.length < limit}
              >
                Next
              </Button>
            </Grid>
          ) : null}
        </div>
      </Grid>
      {data.length > 0 ? (
        <SpeedDial
          ariaLabel="Upload users"
          sx={{ position: "fixed", bottom: 16, right: 16 }}
          icon={<MoreVertIcon />}
        >
           <SpeedDialAction
            icon={<ClearIcon />}
            tooltipTitle="Reset Data"
            onClick={resetFilters}
          />
          {selectedQueryId ? (
            <SpeedDialAction
              icon={<DeleteIcon />}
              tooltipTitle="Delete Query"
              onClick={() => setDeleteQueryOpen(true)}
            />
          ) : null}
          <SpeedDialAction
            icon={<SaveIcon />}
            tooltipTitle="Save Query"
            onClick={() => setSaveQueryOpen(true)}
          />
          <SpeedDialAction
            icon={<DownloadIcon />}
            tooltipTitle="Download Report"
            onClick={handleExport}
          />
          <SpeedDialAction
            icon={<EmailIcon />}
            tooltipTitle="Send Email"
            onClick={() => setComposeOpen(true)}
          />
        </SpeedDial>
      ) : null}
      <ComposeMsgDialog
        open={composeOpen}
        close={() => setComposeOpen(false)}
        handleSend={handleSend}
      />
      <Dialog open={fieldsDialogOpen} onClose={handleCloseFieldsDialog}>
        <DialogTitle>Select Fields</DialogTitle>
        <DialogContent dividers>
          {[...fields]
            .sort((a, b) => {
              const ai =
                defaultOrder.indexOf(a.value) !== -1
                  ? defaultOrder.indexOf(a.value)
                  : defaultOrder.length;
              const bi =
                defaultOrder.indexOf(b.value) !== -1
                  ? defaultOrder.indexOf(b.value)
                  : defaultOrder.length;
              if (ai !== bi) return ai - bi;
              return a.label.localeCompare(b.label);
            })
            .map((f, idx) => (
              <FormControlLabel
                key={f.value}
                control={
                  <Checkbox
                    checked={f.checked}
                    onChange={() => {
                      const stateIndex = fields.findIndex(
                        (field) => field.value === f.value
                      );
                      handleToggleField(stateIndex);
                    }}
                  />
                }
                label={f.label}
                style={{ display: "block", marginBottom: 4 }}
              />
            ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFieldsDialog}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmOpen}>
        <DialogTitle>{sending ? "Sending..." : "Please confirm"}</DialogTitle>

        <DialogContent>
          <Typography>
            Are you sure you want to send this message to {total} people?
          </Typography>
          <Typography>
            ALSO, please make sure that the report has all of the substitution
            variables in your template selected
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={sending}>
            cancel
          </Button>
          <Button onClick={handleConfirmSend}>
            {sending ? <CircularProgress size={20} /> : "Send"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={saveQueryOpen}>
        <DialogTitle>Save Query</DialogTitle>

        <DialogContent>
          <Typography style={{ marginBottom: 20 }}>
            Save this query so that you can easily run it again in the future.
          </Typography>
          <TextField
            value={queryName}
            onChange={(e) => setQueryName(e.target.value)}
            size="small"
            label="Query Name"
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSaveQueryOpen(false);
              setQueryName("");
            }}
          >
            cancel
          </Button>
          <Button
            onClick={handleSaveQuery}
            disabled={queryName.length === 0 ? true : false}
          >
            save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteQueryOpen}>
        <DialogTitle>Delete Query</DialogTitle>

        <DialogContent>
          <Typography style={{ marginBottom: 20 }}>
            Are you sure you want to delete this saved query?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteQueryOpen(false);
            }}
          >
            cancel
          </Button>
          <Button
            onClick={handleDeleteQuery}
          >
            delete it
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default CustomReporting;
